import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MembershipTierPanel = ({ user, onUpgrade }) => {
  const tiers = [
    {
      name: 'Intro',
      level: 1,
      color: 'bg-secondary',
      benefits: ['Basic wellness tracking', 'Community access', 'Monthly newsletter'],
      price: 'Free'
    },
    {
      name: 'All Access',
      level: 2,
      color: 'bg-primary',
      benefits: ['All Intro benefits', 'Expert workshops', 'Priority support', 'Advanced analytics'],
      price: '$29/month'
    },
    {
      name: 'Flex',
      level: 3,
      color: 'bg-accent',
      benefits: ['All Access benefits', 'Personal coaching', 'Custom meal plans', 'Ad spend credits'],
      price: '$59/month'
    },
    {
      name: 'Premium',
      level: 4,
      color: 'bg-gradient-to-r from-accent to-primary',
      benefits: ['All Flex benefits', '1-on-1 sessions', 'VIP events', 'Exclusive content'],
      price: '$99/month'
    }
  ];

  const currentTier = tiers?.find(tier => tier?.name === user?.tier) || tiers?.[0];
  const nextTier = tiers?.find(tier => tier?.level === currentTier?.level + 1);

  const progressToNext = user?.tierProgress || 65;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Award" size={24} className="text-primary" />
        <h2 className="text-xl font-heading font-semibold text-foreground">Membership Tier</h2>
      </div>
      {/* Current Tier Status */}
      <div className="mb-8">
        <div className={`${currentTier?.color} rounded-lg p-6 text-white mb-4`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-heading font-bold">{currentTier?.name}</h3>
              <p className="opacity-90">Current Membership Level</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{currentTier?.level}</div>
              <div className="text-sm opacity-90">Level</div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentTier?.benefits?.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={16} />
                <span className="text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Progress to Next Tier */}
        {nextTier && (
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-heading font-medium text-foreground">
                Progress to {nextTier?.name}
              </h4>
              <span className="text-sm font-medium text-primary">{progressToNext}%</span>
            </div>
            <div className="w-full bg-border rounded-full h-2 mb-3">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressToNext}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Complete wellness activities to advance</span>
              <span>{100 - progressToNext}% remaining</span>
            </div>
          </div>
        )}
      </div>
      {/* Tier Comparison */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">All Membership Tiers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers?.map((tier) => (
            <div 
              key={tier?.name}
              className={`border rounded-lg p-4 transition-wellness ${
                tier?.name === currentTier?.name 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading font-medium text-foreground">{tier?.name}</h4>
                {tier?.name === currentTier?.name && (
                  <div className="w-2 h-2 bg-primary rounded-full animate-breathing"></div>
                )}
              </div>
              <div className="text-2xl font-bold text-primary mb-3">{tier?.price}</div>
              <ul className="space-y-2">
                {tier?.benefits?.slice(0, 3)?.map((benefit, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={12} className="text-success" />
                    <span>{benefit}</span>
                  </li>
                ))}
                {tier?.benefits?.length > 3 && (
                  <li className="text-xs text-muted-foreground">
                    +{tier?.benefits?.length - 3} more benefits
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Upgrade Actions */}
      {nextTier && (
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="default"
            onClick={() => onUpgrade(nextTier?.name)}
            iconName="ArrowUp"
            iconPosition="left"
            className="flex-1"
          >
            Upgrade to {nextTier?.name} - {nextTier?.price}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = '/wellness-marketplace?category=memberships'}
            iconName="ShoppingBag"
            iconPosition="left"
          >
            View All Benefits
          </Button>
        </div>
      )}
      {/* Tier Benefits Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="font-heading font-medium text-foreground mb-3">Your Current Benefits</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3 p-3 bg-success/10 rounded-lg">
            <Icon name="Users" size={20} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Community Access</p>
              <p className="text-xs text-muted-foreground">Full forum participation</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-accent/10 rounded-lg">
            <Icon name="TrendingUp" size={20} className="text-accent" />
            <div>
              <p className="text-sm font-medium text-foreground">Progress Tracking</p>
              <p className="text-xs text-muted-foreground">Advanced analytics</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-primary/10 rounded-lg">
            <Icon name="Calendar" size={20} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">Event Access</p>
              <p className="text-xs text-muted-foreground">Priority registration</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-secondary/10 rounded-lg">
            <Icon name="Gift" size={20} className="text-secondary" />
            <div>
              <p className="text-sm font-medium text-foreground">Rewards Program</p>
              <p className="text-xs text-muted-foreground">Earn points & badges</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipTierPanel;